import { useTriggerLoad } from "@redotech/react-util/load";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import { RedoFileUpload } from "@redotech/redo-web/arbiter-components/file-upload/redo-file-upload";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { parseCsvValues } from "../../../utils/parse-csv-values";

interface UploadCsvModalProps {
  open: boolean;
  onClose: () => void;
  onValuesUploaded: (values: string[]) => void;
  possibleColumnNames?: string[];
  valueLabel?: string;
}

export const UploadCsvModal = memo(function UploadCsvModal({
  open,
  onClose,
  onValuesUploaded,
  possibleColumnNames,
  valueLabel = "values",
}: UploadCsvModalProps) {
  const [file, setFile] = useState<File | null>(null);

  const handleFilesAdded = (files: FileList | null) => {
    if (files && files.length > 0) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFile(files[0]);
    }
  };

  const handleFileDeleted = () => {
    setFile(null);
  };

  const [uploadLoad, doUpload] = useTriggerLoad((signal) =>
    alertOnFailure(`Error uploading ${valueLabel}`)(async () => {
      if (!file) {
        return;
      }

      const values = await parseCsvValues(file, possibleColumnNames);

      if (values.length === 0) {
        toast(`No ${valueLabel} found in the CSV file`, { variant: "warning" });
        return;
      }

      toast(`Successfully uploaded ${values.length} ${valueLabel}`, {
        variant: "success",
      });

      onValuesUploaded(values);
      onClose();
      setFile(null);
    }),
  );

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  const columnNamesText = possibleColumnNames
    ? possibleColumnNames.join(", or ")
    : "any column name";

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={handleClose}
      primaryButton={{
        text: "Upload",
        onClick: doUpload,
        size: "sm",
        pending: uploadLoad.pending,
        disabled: !file,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: handleClose,
        size: "sm",
        disabled: uploadLoad.pending,
      }}
      title="Upload CSV"
    >
      <Flex dir="column" gap="2xl" w="full">
        <RedoFileUpload
          accept="text/csv"
          acceptMessage="CSV"
          files={file ? [file] : []}
          fullWidth
          handleFileDeleted={handleFileDeleted}
          handleFilesAdded={handleFilesAdded}
          nFilesToAccept={1}
        />
        <Text fontSize="sm" textColor="secondary">
          Upload a CSV file with {valueLabel}. The file should include a column
          named {columnNamesText}. If no matching column is found, the first
          column will be used. Only values in the first column will be used.
        </Text>
      </Flex>
    </RedoModal>
  );
});
